import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { OutlineNode } from '../../types';

export interface OutlinePreviewDialogProps {
  outline: OutlineNode[] | null | undefined;
  lang: string;
  headingLevel: 'h1' | 'all-headings';
  children: (openDialog: () => void) => React.ReactNode;
}

function OutlinePreviewDialog({
  outline,
  lang,
  headingLevel,
  children,
}: OutlinePreviewDialogProps) {
  const [open, setOpen] = React.useState(false);

  const handleClose = () => {
    setOpen(false);
  };
  // Utility: build slides array based on headingLevel preference
  const slides = React.useMemo(() => {
    if (!outline || outline.length === 0) return [];

    if (headingLevel === 'h1') {
      // Treat only top-level nodes as slides
      return outline.map((node) => ({ title: node.text, content: node.children || [] }));
    }

    // headingLevel === 'all-headings'
    const collected: Array<{ title: string; content: OutlineNode[] }> = [];

    const traverse = (node: OutlineNode) => {
      collected.push({ title: node.text, content: node.children || [] });
      node.children?.forEach(traverse);
    };

    outline.forEach(traverse);
    return collected;
  }, [outline, headingLevel]);

  return (
    <>
      {children(() => setOpen(true))}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {lang === 'en' ? 'Slides Preview' : '幻灯片预览'}
      </DialogTitle>
      <DialogContent>
        {slides.length > 0 && (
          <Box sx={{ p: 1 }}>
            {renderSlidesPreview(slides, lang)}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {lang === 'en' ? 'Close' : '关闭'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

// Renders the list of slide previews
function renderSlidesPreview(
  slides: Array<{ title: string; content: OutlineNode[] }>,
  lang: string
) {
  return (
    <Stack spacing={3}>
      {slides.map((slide, index) => (
        <Card
          key={`slide-${index}`}
          variant="outlined"
          sx={{
            backgroundColor: 'background.default',
            borderRadius: 1,
          }}
        >
          <CardContent>
            {/* Slide header */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="overline"
                color="primary"
                sx={{ fontWeight: 600 }}
              >
                {lang === 'en' ? `Slide ${index + 1}` : `幻灯片 ${index + 1}`}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mt: 0.5,
                }}
              >
                {slide.title}
              </Typography>
            </Box>

            {/* Slide content */}
            {slide.content.length > 0 && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: 'block', fontWeight: 500 }}
                  >
                    {lang === 'en' ? 'Content:' : '内容：'}
                  </Typography>
                  {renderSlideContent(slide.content)}
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

function renderSlideContent(nodes: OutlineNode[], indentLevel = 0): React.ReactNode {
  if (!nodes) return null;

  return nodes.map((node, index) => {
    const isSubtitle = indentLevel === 0;

    return (
      <Box
        key={`content-${indentLevel}-${index}-${node.text}`}
        sx={{
          ml: indentLevel * 2,
          mb: isSubtitle ? 1 : 0.5,
        }}
      >
        <Typography
          variant={isSubtitle ? 'body1' : 'body2'}
          color={isSubtitle ? 'text.primary' : 'text.secondary'}
          sx={{
            fontWeight: isSubtitle ? 500 : 400,
            display: 'flex',
            alignItems: 'flex-start',
            lineHeight: 1.6,
          }}
        >
          <span
            style={{
              marginRight: '8px',
              color: 'rgba(0, 0, 0, 0.4)',
              minWidth: '16px',
            }}
          >
            {indentLevel === 0 ? '•' : indentLevel === 1 ? '◦' : '▪'}
          </span>
          {node.text}
        </Typography>
        {node.children && node.children.length > 0 &&
          renderSlideContent(node.children, indentLevel + 1)}
      </Box>
    );
  });
}

export default OutlinePreviewDialog; 